import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { Button } from 'ncoded-component-library';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import DraggableCard from 'components/DraggableCard';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import DragIcon from 'icons/Drag.icon';
import PlusIcon from 'icons/Plus.icon';
import XIcon from 'icons/X.icon';
import useMatchMedia from 'hooks/useMatchMedia';
import { ALIGNMENT } from 'models/Global';
import { ORDER_ITEM_TYPE, OrderFormItem } from 'models/WorkflowConfiguration';
import { getArrayLength, getIsArrayEmpty } from 'utils';
import StageFileRow from '../StageFileRow';
import StageMessageStep from '../StageMessageStep';
import StageFilesEmptyState from '../StageFilesEmptyState';
import useStageNodeMeta from '../../hooks/useStageNodeMeta';
import useOpenStageFile from '../../hooks/useOpenStageFile';
import {
  OrderedStageEntry,
  createMessageItem,
  getGapMessageKey,
  getOrderItemKey,
  insertOrderItem,
} from '../../utils/stageFiles';

import './StageFilesOrder.styles.scss';

const DRAG_START_DISTANCE = 6;

// Cards drag from anywhere but their controls and text fields.
const NO_DRAG_SELECTOR = '[data-no-drag], input, textarea';

class CardPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent: event }: React.PointerEvent) =>
        event.isPrimary &&
        event.button === 0 &&
        !(event.target as Element).closest(NO_DRAG_SELECTOR),
    },
  ];
}

type StageFilesOrderProps = {
  className?: string;
  entries: OrderedStageEntry[];
  hasFiles: boolean;
};

const StageFilesOrder: React.FC<StageFilesOrderProps> = (props) => {
  const { className, entries, hasFiles } = props;

  const {
    input: { value: orderItems, onChange },
  } = useField<OrderFormItem[]>('orderItems');

  // One message is edited at a time.
  const [openMessageKey, setOpenMessageKey] = useState<string>();

  const [isDragging, setIsDragging] = useState(false);

  const { t } = useTranslation();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const { getOrderEntryMeta } = useStageNodeMeta();

  const openStageFile = useOpenStageFile();

  // The distance lets clicks inside a card, e.g. opening a message, through.
  const sensors = useSensors(
    useSensor(CardPointerSensor, {
      activationConstraint: { distance: DRAG_START_DISTANCE },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Moves within the whole order, so files still loading keep their place.
  const moveItem = useCallback(
    (fromKey: string, toKey: string) => {
      const items = orderItems || [];
      const keys = items.map(getOrderItemKey);

      onChange(arrayMove(items, keys.indexOf(fromKey), keys.indexOf(toKey)));
    },
    [onChange, orderItems],
  );

  const onDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setIsDragging(false);

      if (!over || active.id === over.id) return;

      moveItem(`${active.id}`, `${over.id}`);
    },
    [moveItem],
  );

  const removeItem = useCallback(
    (key: string) =>
      onChange(
        (orderItems || []).filter((item) => getOrderItemKey(item) !== key),
      ),
    [onChange, orderItems],
  );

  // Opens the new message in place; with no key it goes at the end.
  // A gap holds one message, so one already there opens instead.
  const addMessage = useCallback(
    (beforeKey?: string) => {
      const items = orderItems || [];
      const gapMessageKey = getGapMessageKey(items, beforeKey);

      if (gapMessageKey) {
        setOpenMessageKey(gapMessageKey);
        return;
      }

      const message = createMessageItem();

      onChange(insertOrderItem(items, message, beforeKey));
      setOpenMessageKey(message.key);
    },
    [onChange, orderItems],
  );

  const classes = classNames(
    'stage-files-order',
    { 'stage-files-order--dragging': isDragging },
    className,
  );

  if (getIsArrayEmpty(entries)) {
    return (
      <StageFilesEmptyState
        title={t('nothingInTheOrderYet')}
        description={
          !hasFiles
            ? t('nothingInOrderNoFilesDescription')
            : isAbovePhablet
            ? t('nothingInOrderDescription')
            : t('nothingInOrderMobileDescription')
        }
      />
    );
  }

  const lastIndex = getArrayLength(entries) - 1;

  // Prev and next in the preview walk the order.
  const orderFiles = entries.flatMap((entry) =>
    entry.type === ORDER_ITEM_TYPE.FILE ? [entry.node] : [],
  );

  const getRowMenuOptions = (
    entry: OrderedStageEntry,
    index: number,
    isRemovable: boolean,
  ) => [
    {
      label: t('moveUp'),
      disabled: !index,
      onClick: () => moveItem(entry.key, entries[index - 1].key),
    },
    {
      label: t('moveDown'),
      disabled: index === lastIndex,
      onClick: () => moveItem(entry.key, entries[index + 1].key),
    },
    // Below a message is the same gap, so it could only reopen that message.
    ...(entry.type === ORDER_ITEM_TYPE.FILE
      ? [
          {
            label: t('addMessageBelow'),
            onClick: () => addMessage(entries[index + 1]?.key),
          },
        ]
      : []),
    {
      label: t('removeFromOrder'),
      alert: true,
      disabled: !isRemovable,
      onClick: () => removeItem(entry.key),
    },
  ];

  const renderRow = (
    entry: OrderedStageEntry,
    index: number,
    handle?: React.ReactNode,
  ) => {
    const { key } = entry;
    const isRemovable =
      entry.type === ORDER_ITEM_TYPE.MESSAGE || entry.isRemovable;

    // Mobile moves items from a row menu instead of dragging.
    const action = isAbovePhablet ? (
      <Button
        type="button"
        variant="icon"
        icon={XIcon}
        className="stage-files-order__remove"
        aria-label={t('removeFromOrder')}
        disabled={!isRemovable}
        onClick={() => removeItem(key)}
      />
    ) : (
      <ThreeDotsDropdown
        alignment={ALIGNMENT.VERTICAL}
        withBorder={false}
        options={getRowMenuOptions(entry, index, isRemovable)}
      />
    );

    if (entry.type === ORDER_ITEM_TYPE.MESSAGE) {
      const isOpen = openMessageKey === key;

      return (
        <StageMessageStep
          className="stage-files-order__item"
          entry={entry}
          number={index + 1}
          isOpen={isOpen}
          handle={handle}
          action={action}
          onToggle={() => setOpenMessageKey(isOpen ? undefined : key)}
          onClose={() => setOpenMessageKey(undefined)}
        />
      );
    }

    return (
      <StageFileRow
        className="stage-files-order__item"
        node={entry.node}
        meta={getOrderEntryMeta(entry)}
        prefix={
          <>
            {handle}
            <span className="stage-files-order__number">{index + 1}</span>
          </>
        }
        action={action}
        onNameClick={() => openStageFile(entry.node, orderFiles)}
      />
    );
  };

  if (!isAbovePhablet) {
    return (
      <div className="stage-files-order__wrapper">
        <ul className={classes}>
          {entries.map((entry, index) => (
            <li key={entry.key}>{renderRow(entry, index)}</li>
          ))}
        </ul>

        <button
          type="button"
          className="stage-files-order__add-at-end"
          onClick={() => addMessage()}
        >
          <span className="stage-files-order__add-at-end-label">
            <PlusIcon />
            {t('addAMessage')}
          </span>
          <span className="stage-files-order__add-at-end-hint">
            {t('youCanMoveItAnywhereInTheOrder')}
          </span>
        </button>
      </div>
    );
  }

  // Sits before an item, or after the last one when there is no key.
  const renderInsertionPoint = (beforeKey?: string) => (
    <li className="stage-files-order__insert">
      <button
        type="button"
        aria-label={t('addMessageHere')}
        onClick={() => addMessage(beforeKey)}
      >
        <PlusIcon />
        {t('message')}
      </button>
    </li>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragStart={() => setIsDragging(true)}
      onDragCancel={() => setIsDragging(false)}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={entries.map(({ key }) => key)}
        strategy={verticalListSortingStrategy}
      >
        <ul className={classes}>
          {entries.map((entry, index) => (
            <React.Fragment key={entry.key}>
              {renderInsertionPoint(entry.key)}
              <li>
                {/* The whole card drags; the keyboard starts from the handle. */}
                <DraggableCard id={entry.key}>
                  {({ attributes, listeners, setActivatorNodeRef }) => (
                    <div className="stage-files-order__card" {...listeners}>
                      {renderRow(
                        entry,
                        index,
                        <span
                          ref={setActivatorNodeRef}
                          className="stage-files-order__handle"
                          aria-label={t('dragToReorder')}
                          {...attributes}
                        >
                          <DragIcon />
                        </span>,
                      )}
                    </div>
                  )}
                </DraggableCard>
              </li>
            </React.Fragment>
          ))}
          {renderInsertionPoint()}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export default StageFilesOrder;
